import React, { useMemo, useState } from 'react';
import { Modal, Space, Collapse, Checkbox, Input, Button } from 'antd';
import { FaRegThumbsUp, FaRegThumbsDown, FaEnvelope, FaHeadphones } from 'react-icons/fa';
import { FaAngleDown } from 'react-icons/fa6';
import { useTranslation } from 'react-i18next';
import useArticle from 'vl-common/src/hooks/useArticle';
import { errorModal } from 'vl-common/src/components/Modals';
import * as actions from '@actions';
import useUser from '@vl-core/hooks/useUser';
import { useDispatch } from 'react-redux';
import useRuntimeConfig from '@vl-core/useRuntimeConfig';

type FeedbackState = 'positive' | 'negative' | null;

const extractCheckboxContent = (htmlString: string): string[] => {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    const listItems = doc.querySelectorAll('li');

    return Array.from(listItems)
      .map((element) => element.textContent?.trim())
      .filter((text): text is string => !!text);
  } catch (error) {
    console.error('Error parsing list items:', error);
    return [];
  }
};

const TestCallResultsModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const { t } = useTranslation('common');
  const { Panel } = Collapse;
  const { APP_VERSION } = useRuntimeConfig();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<FeedbackState>(null);
  const [checkedIssues, setCheckedIssues] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const { TextArea } = Input;
  const dispatch = useDispatch();
  const user = useUser();
  const isTest = APP_VERSION !== 'NPROD' && APP_VERSION !== 'PROD' ? '- TEST EMAIL' : '';

  const checkboxArticle = useArticle('TEST_CALL_CHECKBOX');
  const extractedCheckboxOptions = useMemo(
    () => (checkboxArticle?.body ? extractCheckboxContent(checkboxArticle.body) : []),
    [checkboxArticle?.body]
  );

  const troubleshootingContent = useArticle('TEST_CALL_TROUBLESHOOT');

  const handleOk = async () => {
    if (!feedback) return;

    setIsSubmitting(true);

    try {
      const successValue = feedback === 'positive' ? 1 : 0;

      await dispatch(
        actions.postTestCallFeedback(
          user?.user_guid,
          successValue,
          feedback,
          JSON.stringify(checkedIssues),
          notes.trim()
        )
      );

      if (feedback === 'negative') {
        const subjectField = `Clinician: ${user.first_name} ${user.last_name} - Test Call issues ${isTest}`;
        const descriptionField =
          `Clinician: ${user.first_name} ${user.last_name} is experiencing technical issues when using the Test Call feature.\n\n` +
          `ISSUES REPORTED: ${checkedIssues.join(', ')}\n\n` +
          (notes.trim() ? `ADDITIONAL NOTES: ${notes.trim()}\n\n` : '') +
          `Clinician Email: ${user.email}`;

        await dispatch(actions.postTestCallSupportEmail(subjectField, descriptionField));
      }

      setIsSubmitting(false);
      onClose();
    } catch {
      errorModal({
        title: 'Error reporting issue',
        content: 'There has been an error reporting your issue. Please try again.'
      });
      setIsSubmitting(false);
    }
  };

  const isNegativeFeedback = feedback === 'negative';
  const isOkDisabled = !feedback || (isNegativeFeedback && checkedIssues.length === 0 && notes.trim() === '');

  const getBorderStyle = (currentSelection: FeedbackState) => {
    const isSelected = currentSelection === feedback;
    const borderWidth = isSelected ? '2px' : '1px';

    return {
      cursor: 'pointer',
      padding: '30px 50px',
      border: `${borderWidth} solid ${isSelected ? 'var(--productPrimary)' : '#d9d9d9'}`,
      backgroundColor: `${isSelected ? '#e6f0fa' : ''}`,
      borderRadius: '12px',
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      transition: 'border-color 0.2s ease, border-width 0.2s ease',
      width: '200px',
      minWidth: '100px'
    };
  };

  const CustomFooter = () => {
    const footerDescription = t(
      'When you submit feedback, a support ticket is automatically generated, allowing our technical support team to investigate the issue'
    );

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          width: '100%',
          paddingTop: '8px',
          paddingBottom: '16px'
        }}
      >
        {isNegativeFeedback && (
          <div
            style={{
              marginRight: 'auto',
              textAlign: 'left',
              fontSize: '12px',
              color: 'black',
              maxWidth: '70%'
            }}
          >
            {footerDescription}
          </div>
        )}

        <Button
          data-testid="send-feedback-button"
          type="primary"
          onClick={handleOk}
          loading={isSubmitting}
          disabled={isOkDisabled}
        >
          {t('Send Feedback')}
        </Button>
      </div>
    );
  };

  const handleCheckboxChange = (checkedValue: string) => {
    setCheckedIssues((prev) =>
      prev.includes(checkedValue) ? prev.filter((v) => v !== checkedValue) : [...prev, checkedValue]
    );
  };

  return (
    <Modal
      title={t('How was the call quality?')}
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      okText={t('Send Feedback')}
      centered
      width={700}
      footer={<CustomFooter />}
      closable={true}
      cancelButtonProps={{ style: { display: 'none' } }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: '24px'
        }}
      >
        <Space size={25} style={{ marginTop: '16px' }}>
          {/* noprettier */}
          <div // NOSONAR
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === '') setFeedback('negative');
            }}
            style={getBorderStyle('negative')}
            onClick={() => setFeedback('negative')}
          >
            <FaRegThumbsDown style={{ fontSize: '40px', color: '#ff7700', marginBottom: '4px' }} />
            <span style={{ fontSize: '14px', fontWeight: 'bold', textAlign: 'center', marginTop: '12px' }}>
              {t('Had Issues')}
            </span>
          </div>

          {/* noprettier */}
          <div // NOSONAR
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === '') setFeedback('positive');
            }}
            style={getBorderStyle('positive')}
            onClick={() => setFeedback('positive')}
          >
            <FaRegThumbsUp style={{ fontSize: '40px', color: '#066ccc', marginBottom: '4px' }} />
            <span style={{ fontSize: '14px', fontWeight: 'bold', textAlign: 'center', marginTop: '12px' }}>
              {t('Good')}
            </span>
          </div>
        </Space>
      </div>

      {isNegativeFeedback && (
        <Space direction="vertical" style={{ width: '100%', marginTop: '16px' }}>
          <Collapse
            bordered={true}
            expandIconPosition="end"
            style={{ width: '100%', marginBottom: '24px' }}
            expandIcon={() => <FaAngleDown style={{ fontSize: '16px' }} />}
          >
            <Panel header={t('ⓘ Try these troubleshooting tips first and retest')} key="1">
              <div
                className="troubleshooting-content"
                dangerouslySetInnerHTML={{ __html: troubleshootingContent?.body || '' }}
              />
            </Panel>
          </Collapse>

          <label style={{ fontWeight: 'bold', marginTop: '10px', marginBottom: '4px' }}>
            {t("What could've been better?")}
          </label>

          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {extractedCheckboxOptions.map((option) => (
              <div key={option} style={{ width: '50%', marginBottom: '8px' }}>
                <Checkbox
                  value={option}
                  checked={checkedIssues.includes(option)}
                  onChange={() => handleCheckboxChange(option)}
                >
                  {option}
                </Checkbox>
              </div>
            ))}
          </div>
          <TextArea
            rows={2}
            placeholder={t('Other, please specify...')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            style={{ marginTop: '12px' }}
          />
          <div
            style={{
              display: 'flex',
              width: '100%',
              gap: '16px',
              marginTop: '24px',
              marginBottom: '24px',
              alignItems: 'stretch'
            }}
          >
            <div
              style={{
                flex: 1,
                padding: '16px',
                border: '1px solid #ff7700',
                borderRadius: '8px',
                backgroundColor: '#faeee3',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
            >
              <FaEnvelope color="#ff7700" size={20} style={{ marginBottom: '8px' }} />
              <span style={{ fontSize: '14px', lineHeight: '1.4' }}>
                {t("If you're unable to run an appointment, email ")}
                <a href="mailto:rescheduling@hbsuk.co.uk" style={{ textDecoration: 'none' }}>
                  <strong>{t('rescheduling@hbsuk.co.uk')}</strong>
                </a>
                {t(' for assistance')}
              </span>
            </div>

            <div
              style={{
                flex: 1,
                padding: '16px',
                border: '1px solid #066ccc',
                borderRadius: '8px',
                backgroundColor: '#e6f0fa',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
            >
              <FaHeadphones color="#066ccc" size={20} style={{ marginBottom: '8px' }} />
              <span style={{ fontSize: '14px', lineHeight: '1.4' }}>
                {t('For video-only issues, please consider')}
                <br />
                <strong>{t('an audio call')}</strong>
                {t(' as a temporary workaround')}
              </span>
            </div>
          </div>
        </Space>
      )}
    </Modal>
  );
};

export default TestCallResultsModal;
